import { t } from "i18next";
import moment from "moment";
import i18n from "src/i18n";

import { CONSTANTS } from "@constants/index";
import { SupportedLang } from "@constants/user";
import { DateFormats } from "@enums/shared.enum";
import API_SERVICES from "@services/index";
import dashboardSlice from "@slices/dashboard.slice";
import localSlice from "@slices/local.slice";
import userSlice from "@slices/user.slice";
import { store } from "@store/index";

import { resetAnalyticsSession, trackUserLoggedOut } from "./analytics";
import commonUtils from "./common.utils";

export const compareDates = (date: string, tab: string, startTime?: string) => {
  const { DATE_FORMATS, RESERVATION_TABS } = CONSTANTS;

  const hasTime = !!startTime;

  if (hasTime) {
    const bookingDateTime = moment(
      `${date}T${startTime}`,
      `${DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT}THH:mm`
    );
    const now = moment();

    switch (tab) {
      case RESERVATION_TABS.TODAY: {
        const isTodayDate = moment(
          date,
          DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT
        ).isSame(now, DateFormats.Day);
        return isTodayDate && bookingDateTime.isAfter(now);
      }
      case RESERVATION_TABS.FUTURE:
        return moment(date, DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT).isAfter(
          now,
          DateFormats.Day
        );

      case RESERVATION_TABS.PAST: {
        const isBeforeToday = moment(
          date,
          DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT
        ).isBefore(now, DateFormats.Day);
        const isTodayPassed =
          moment(date, DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT).isSame(
            now,
            DateFormats.Day
          ) && bookingDateTime.isSameOrBefore(now);
        return isBeforeToday || isTodayPassed;
      }
      default:
        return false;
    }
  }

  const momentDate = moment(
    date,
    DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT
  ).startOf(DateFormats.Day);
  const today = moment().startOf(DateFormats.Day);
  switch (tab) {
    case RESERVATION_TABS.TODAY:
      return momentDate.isSame(today, DateFormats.Day);
    case RESERVATION_TABS.FUTURE:
      return momentDate.isAfter(today, DateFormats.Day);
    case RESERVATION_TABS.PAST:
      return momentDate.isBefore(today, DateFormats.Day);
    default:
      return false;
  }
};

export const getTabWithBookedDate = (date: string) => {
  const { DATE_FORMATS, RESERVATION_TABS } = CONSTANTS;

  const momentDate = moment(date, [
    DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT,
    "DD.MM.YYYY",
    "DD-MM-YYYY",
  ]).startOf(DateFormats.Day);

  if (!momentDate.isValid()) return undefined;

  const today = moment().startOf(DateFormats.Day);

  if (momentDate.isSame(today, DateFormats.Day)) return RESERVATION_TABS.TODAY;
  else if (momentDate.isAfter(today, DateFormats.Day))
    return RESERVATION_TABS.FUTURE;
  else if (momentDate.isBefore(today, DateFormats.Day))
    return RESERVATION_TABS.PAST;
};

export const logout = async (
  closeModal: () => void,
  showToaster: boolean = true
) => {
  trackUserLoggedOut();
  await API_SERVICES.USER.LOG_OUT();
  closeModal();
  store.dispatch(localSlice.actions.clearStorage(null));
  store.dispatch(dashboardSlice.actions.resetDashboard(null));
  localStorage.removeItem(CONSTANTS.LOCAL_STORAGE.RESERVATIONS_SEARCH_QUERY);
  resetAnalyticsSession();
  if (showToaster) {
    commonUtils.toastService(
      t("logoutSuccessMsg"),
      CONSTANTS.TOAST_TYPE.SUCCESS
    );
  }
};

export const applyBrowserLanguage = (updateStore: boolean = true): void => {
  const SUPPORTED_LANGS: SupportedLang[] = ["en", "de", "it", "fr"];

  const storedLang = localStorage
    .getItem("i18nextLng")
    ?.split("-")[0]
    .toLowerCase();
  const browserLang = window.navigator.language.split("-")[0].toLowerCase();

  const resolvedLang: SupportedLang =
    storedLang && SUPPORTED_LANGS.includes(storedLang as SupportedLang)
      ? (storedLang as SupportedLang)
      : SUPPORTED_LANGS.includes(browserLang as SupportedLang)
        ? (browserLang as SupportedLang)
        : "de";

  i18n.changeLanguage(resolvedLang);
  if (updateStore) {
    store.dispatch(
      userSlice.actions.userSelectedLanguage({
        active_language: resolvedLang,
      })
    );
  }
};
